import { useSWRData } from "@root/shared/hooks/useSWRData";
import { useMemo, useState } from "react";

import { convertToChoiceGroups, convertToPopularAreaChoices } from "./utils";
import { type ChoiceGroup, type Choice } from "./types";

export type UseAreaDataParams = {
  prefectureValue: string;
  citiesUrl: string;
  linesUrl: string;
  popularAreasUrl: string;
  isBiyo: boolean;
  onError: (message: string) => void;
};

export type AreaData = {
  popularAreaChoices: Choice[];
  cityChoices: ChoiceGroup[];
  lineChoices: ChoiceGroup[];
  isLoading: boolean;
  isError: boolean;
};

export const useAreaData = (params: UseAreaDataParams): AreaData => {
  const { prefectureValue, citiesUrl, linesUrl, popularAreasUrl, isBiyo, onError } = params;

  const [isError, setIsError] = useState<boolean>(false);

  const handleError = (message: string) => {
    setIsError(true);
    onError(message);
  };

  const { data: popularAreaData, isValidating: isPopularAreaLoading } = useSWRData<string[][]>({
    url: popularAreasUrl,
    params: { prefecture: prefectureValue },
    options: {
      onError: () => handleError("人気エリアデータを取得できませんでした"),
    },
    shouldFetch: !!prefectureValue && isBiyo,
  });

  const { data: cityData, isValidating: isCityLoading } = useSWRData<
    (string | null | string[][])[][]
  >({
    url: citiesUrl,
    // NOTE: 既存実装に揃えてtreeとcodeのパラメータを固定値で指定
    params: { prefecture: prefectureValue, tree: "1", code: "1" },
    options: {
      onError: () => handleError("市区町村データを取得できませんでした"),
    },
    shouldFetch: !!prefectureValue,
  });

  const { data: lineData, isValidating: isLineLoading } = useSWRData<
    (string | null | string[][])[][]
  >({
    url: linesUrl,
    // NOTE: 既存実装に揃えてstationsパラメータを固定値で指定
    params: { prefecture: prefectureValue, stations: "1" },
    options: {
      onError: () => handleError("路線データを取得できませんでした"),
    },
    shouldFetch: !!prefectureValue,
  });

  const popularAreaChoices = useMemo(
    () => convertToPopularAreaChoices(popularAreaData ?? []),
    [popularAreaData],
  );

  const cityChoices = useMemo(() => convertToChoiceGroups(cityData ?? []), [cityData]);

  const lineChoices = useMemo(() => convertToChoiceGroups(lineData ?? []), [lineData]);

  const isLoading = isPopularAreaLoading || isCityLoading || isLineLoading;

  return {
    popularAreaChoices,
    cityChoices,
    lineChoices,
    isLoading,
    isError,
  };
};
